import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react"
import { Grid, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { OnBehalfCompletedDialog } from "../../../../shared/components/createOrder/hooks/useCreateOrderUser"
import { OutlookAttendeesOptInProvider } from "../../../../shared/components/createOrder/OutlookAttendeesOptInContext"
import {
    CreateOrderDialogMode,
    CreateOrderDialogType,
    CreateOrderDialogFlow,
    CreateOrderDialogFlowMode,
    CreateOrderFlowType,
    CreateOrderOutlookFlowType,
} from "../../../../shared/components/createOrder/createOrderTypes"
import type { OnBehalfOfUser } from "../../../../shared/components/createOrder/createOrderTypes"
import { UserType } from "../../../login/loginTypes"
import { CreateOrderDialog } from "../../../../shared/components/createOrder/CreateOrderDialog"
import { CreateOrderType } from "../../../../shared/components/createOrder/helpers"

import { BookingMessageReason, BookingMessageType, postBookingResultToOutlook } from "./bookingMessaging"
import { OutlookBookingSuccessArgs, SessionValue } from "../outlookCreateOrderTypes"
import { isOutlookMeetingValue } from "../helpers"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useEnvironment } from "../../../environment/envHooks"

const useStyles = makeStyles()(() => ({
    wrap: {
        position: "relative",
        height: "100vh",
        overflow: "hidden",
    },
}))

type OutlookCreateOrderProps = Readonly<{
    orderId?: number
    mode: CreateOrderDialogMode
    flowParam: string | null
    modeParam: string | null
    onBehalf: OnBehalfCompletedDialog
    setOnBehalf: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
    parentOrigin: string
    session?: SessionValue[]
    initialShareOutlookAttendees: boolean
    isSharedCalendar: boolean | undefined
    outlookInviteeCount: number | undefined
    onBehalfOf: OnBehalfOfUser | undefined
}>

export function OutlookCreateOrderContent({
    orderId,
    mode,
    flowParam,
    modeParam,
    onBehalf,
    setOnBehalf,
    parentOrigin,
    session,
    initialShareOutlookAttendees,
    isSharedCalendar,
    outlookInviteeCount,
    onBehalfOf,
}: OutlookCreateOrderProps) {
    const { classes } = useStyles()
    const { currentEnv } = useEnvironment()
    const { l10n } = useAppLocalization()
    // "Share Outlook attendees" opt-in. Honoured only in edit mode; create always starts off. The state lives
    // here so it survives the toggle's remounts; the ref mirrors it for reads at message-send time, and the
    // value is returned to the add-in on save/close.
    const isEditMode = mode === CreateOrderDialogMode.EDIT
    const initialShare = isEditMode ? initialShareOutlookAttendees : false
    const [shareOutlookAttendees, setShareOutlookAttendees] = useState(initialShare)
    const shareOutlookAttendeesRef = useRef(initialShare)
    shareOutlookAttendeesRef.current = shareOutlookAttendees

    const isOutlookVSTO = flowParam === CreateOrderOutlookFlowType.VSTO

    const isOutlookFlow = useMemo(() => {
        return flowParam === CreateOrderOutlookFlowType.WEB || isOutlookVSTO
    }, [flowParam])

    const flowMode: CreateOrderDialogFlowMode | null = useMemo(() => {
        if (isOutlookVSTO) return CreateOrderDialogFlowMode.MEETING_SERVICE
        if (
            modeParam === CreateOrderDialogFlowMode.MEETING_SERVICE ||
            modeParam === CreateOrderDialogFlowMode.RESOURCE_BOOKING
        ) {
            return modeParam
        }
        return null
    }, [isOutlookFlow, modeParam])

    const sessionOrder = useMemo(() => {
        const order = session?.find(isOutlookMeetingValue)?.order

        if (!order?.meetingDetails?.isPrivate || order.meetingDetails.meetingName) return order

        // The add-in withholds a private meeting's subject, and the field is required — seed a
        // neutral name so the author is not asked to invent one.
        return {
            ...order,
            meetingDetails: {
                ...order.meetingDetails,
                meetingName: l10n.getString("create-order-outlook-private-meeting-default-name"),
            },
        }
    }, [session, l10n])

    const flowConfig: CreateOrderDialogFlow | undefined = useMemo(() => {
        if (!isOutlookFlow) return undefined
        return {
            flow: CreateOrderFlowType.OUTLOOK,
            mode: flowMode,
            vstoFlowParam: isOutlookVSTO ? CreateOrderOutlookFlowType.VSTO : undefined,
            order: sessionOrder,
            calendarAppointmentId: sessionOrder?.meetingDetails?.calendarAppointmentId,
            isSharedCalendar,
            outlookInviteeCount,
            // admin logins keep their own on-behalf flow, which switches the request user instead
            onBehalfOf: onBehalf.userType === UserType.USER ? onBehalfOf : undefined,
        }
    }, [
        isOutlookVSTO,
        isOutlookFlow,
        modeParam,
        sessionOrder,
        isSharedCalendar,
        outlookInviteeCount,
        onBehalf.userType,
        onBehalfOf,
    ])

    const activeOrderTab = useMemo(() => {
        return flowMode === CreateOrderDialogFlowMode.MEETING_SERVICE
            ? CreateOrderType.MEETING_SERVICE
            : CreateOrderType.RESOURCE_BOOKING
    }, [flowMode])

    // Provided to the toggle so it can render/subscribe without reading the create-order form config (which
    // throws when the shared booking dialog is opened outside the create-order flow, e.g. from RoomFinder).
    const isOutlookWebFlow = isOutlookFlow && !isOutlookVSTO
    // `outlookAttendeeCount` is the field the add-in sends, but the session model on the backend does not
    // keep it, so in practice the headcount survives only as `deliveryDetails.sittings` (same number, both
    // are `attendees.length`). Falling back to it also renders the toggle for an add-in too old to answer
    // the OUTLOOK_ATTENDEES_REQUEST handshake; the toggle's own request timeout covers that.
    const outlookAttendeeCount =
        sessionOrder?.meetingDetails?.outlookAttendeeCount ?? sessionOrder?.deliveryDetails?.sittings ?? 0

    const [isFinished, setIsFinished] = useState(false)

    const handleCloseDialog = () => {
        try {
            postBookingResultToOutlook(
                {
                    type: BookingMessageType.DIALOG_CLOSE,
                    reason: BookingMessageReason.DIALOG_CLOSE_BY_USER,
                    shareOutlookAttendees: shareOutlookAttendeesRef.current,
                },
                parentOrigin,
                currentEnv,
            )
        } catch (e) {
            console.error("Failed to post dialog:close to Outlook:", e)
        }

        // the VSTO add-in has no Office dialog the message could close
        if (isOutlookVSTO) setIsFinished(true)
    }

    const handleOutlookBookingCompleted = useCallback(
        async ({ startDateTime, endDateTime, meetingLocation }: OutlookBookingSuccessArgs) => {
            await postBookingResultToOutlook(
                {
                    type: BookingMessageType.BOOKING_COMPLETED,
                    startDateTime,
                    endDateTime,
                    meetingLocation,
                    shareOutlookAttendees: shareOutlookAttendeesRef.current,
                },
                parentOrigin,
                currentEnv,
            )
        },
        [parentOrigin],
    )

    return (
        <Grid container className={classes.wrap}>
            {isFinished ? (
                <Grid container flexDirection="column" alignItems="center" justifyContent="center">
                    <LocalizedStrict id="create-order-outlook-finish-step">
                        <Typography variant="h3">You can now return to Outlook</Typography>
                    </LocalizedStrict>
                </Grid>
            ) : (
                <OutlookAttendeesOptInProvider
                    value={{
                        shareRef: shareOutlookAttendeesRef,
                        share: shareOutlookAttendees,
                        setShare: setShareOutlookAttendees,
                        isEdit: isEditMode,
                        initialShare,
                        isOutlookWebFlow,
                        outlookAttendeeCount,
                    }}
                >
                    <CreateOrderDialog
                        open
                        orderId={orderId ? +orderId : undefined}
                        mode={mode}
                        flowConfig={flowConfig}
                        onClose={handleCloseDialog}
                        activeOrderTab={activeOrderTab}
                        handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                        isOnBehalfCompleted={onBehalf}
                        setIsOnBehalfCompleted={setOnBehalf}
                        dialogType={CreateOrderDialogType.ORDER}
                    />
                </OutlookAttendeesOptInProvider>
            )}
        </Grid>
    )
}
